/**
 * Reunification Event Report — inline summary panel + JSON export trigger.
 *
 * Per ADR-064 §Domain 6 line 1559, after-action reports are *exports*,
 * not stored entities. This panel renders the ``event_summary`` and
 * ``performance_metrics`` sections inline (so operators can read the
 * numbers without saving a file) and surfaces an Export button that
 * downloads the full report via the ``download_report`` GET endpoint.
 *
 * PII gate (ADR-099 §Layer 5 / FERPA): the "Include student PII" checkbox
 * defaults to off. Flipping it on opens a confirmation dialog because
 * downloading with PII writes an ``EventAssignmentLog`` row with
 * ``action="report.exported_with_pii"`` on the backend — operators
 * should know about that side-effect before triggering it.
 *
 */
import { useMemo, useState } from 'react';
import {
  Box,
  Center,
  Checkbox,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent, DialogComponent, SectionCard } from '@themes/aegix';
import { MdDownload } from 'react-icons/md';
import {
  useEventReport,
  useDownloadEventReport,
} from '../../../hooks/queries/useReunification';
import { toaster } from '../../ui/toaster';
import { DetailRow } from '../DetailRow';
import type { ReportSection } from '../../../types/ReunificationTypes';

interface EventReportPanelProps {
  eventId: string;
}

/**
 * Sections rendered inline. Kept narrow on purpose — full participant
 * rosters are PII-bearing and surface separately (Students / Guardians /
 * Staff side-menu items).
 */
const INLINE_SECTIONS: ReportSection[] = ['executive_summary', 'metrics'];

function formatMinutes(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '---';
  if (value === Math.floor(value)) return `${value}m`;
  return `${value.toFixed(1)}m`;
}

function formatPct(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '---';
  // The backend emits accountability/release/verification rates as a
  // 0-100 number (see ReunificationMetricsService.get_real_time_metrics).
  return `${value.toFixed(1)}%`;
}

function formatCount(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '---';
  return String(value);
}

function formatDateTime(value: unknown): string {
  if (typeof value !== 'string') return '---';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export function EventReportPanel({ eventId }: EventReportPanelProps) {
  // PII opt-in is panel-local state — never persisted, never inherited
  // across panel mounts. Default false per ADR-099 §Layer 5.
  const [includePii, setIncludePii] = useState(false);
  const [pendingPiiConfirm, setPendingPiiConfirm] = useState(false);

  const reportQuery = useEventReport(eventId, {
    include_sections: INLINE_SECTIONS,
    include_pii: false,
  });
  const downloadMutation = useDownloadEventReport(eventId);

  const eventSummary = reportQuery.data?.event_summary;
  const performanceMetrics = reportQuery.data?.performance_metrics;

  // Format inline values defensively — the backend types each section as
  // a generic object so the keys could be absent in older runtime
  // payloads (e.g. before a metrics section update). Treat ``---`` as
  // the canonical "no data" sentinel matching EventDetailOverview.
  const summaryRows = useMemo(() => {
    if (!eventSummary) return null;
    return [
      {
        label: 'Incident Type',
        value: String((eventSummary as Record<string, unknown>).incident_type ?? '---'),
        dataE2e: 'event-report-incident-type',
      },
      {
        label: 'Drill',
        value: (eventSummary as Record<string, unknown>).is_drill ? 'Yes' : 'No',
        dataE2e: 'event-report-is-drill',
      },
      {
        label: 'Status',
        value: String((eventSummary as Record<string, unknown>).status ?? '---'),
        dataE2e: 'event-report-status',
      },
      {
        label: 'Started',
        value: formatDateTime((eventSummary as Record<string, unknown>).started_at),
        dataE2e: 'event-report-started',
      },
      {
        label: 'Completed',
        value: formatDateTime((eventSummary as Record<string, unknown>).completed_at),
        dataE2e: 'event-report-completed',
      },
      {
        label: 'Duration',
        value: formatMinutes((eventSummary as Record<string, unknown>).duration_minutes),
        dataE2e: 'event-report-duration',
      },
      {
        label: 'Total Participants',
        value: formatCount(
          (eventSummary as Record<string, unknown>).total_participants,
        ),
        dataE2e: 'event-report-participants',
      },
    ];
  }, [eventSummary]);

  const metricsRows = useMemo(() => {
    if (!performanceMetrics) return null;
    const m = performanceMetrics as Record<string, unknown>;
    return [
      {
        label: 'Accountability Rate',
        value: formatPct(m.accountability_rate),
        dataE2e: 'event-report-accountability-rate',
      },
      {
        label: 'Release Rate',
        value: formatPct(m.release_rate),
        dataE2e: 'event-report-release-rate',
      },
      {
        label: 'Verification Rate',
        value: formatPct(m.verification_rate),
        dataE2e: 'event-report-verification-rate',
      },
      {
        label: 'Avg Time to Release',
        value: formatMinutes(m.avg_time_to_release),
        dataE2e: 'event-report-avg-release',
      },
    ];
  }, [performanceMetrics]);

  const handlePiiToggle = (next: boolean) => {
    if (next) {
      // Show confirmation dialog before flipping on — the actual flip
      // happens in handlePiiConfirm so the user can back out.
      setPendingPiiConfirm(true);
    } else {
      setIncludePii(false);
    }
  };

  const handlePiiConfirm = () => {
    setIncludePii(true);
    setPendingPiiConfirm(false);
  };

  const handlePiiCancel = () => {
    setPendingPiiConfirm(false);
  };

  const handleExport = () => {
    downloadMutation.mutate(
      {
        // Full report on export — caller decides PII via the gated
        // checkbox above. Inline panel keeps the lighter
        // ``INLINE_SECTIONS`` set.
        include_sections: [
          'executive_summary',
          'metrics',
          'timeline',
          'release_log',
          ...(includePii
            ? (['student_roster', 'guardian_roster', 'staff_roster'] as ReportSection[])
            : []),
        ],
        include_pii: includePii,
      },
      {
        onSuccess: (didDownload) => {
          if (didDownload) {
            toaster.create({
              title: 'Report exported',
              description: includePii
                ? 'JSON downloaded. A FERPA audit-log entry was written.'
                : 'JSON downloaded.',
              type: 'success',
            });
          } else {
            toaster.create({
              title: 'Export was empty',
              description: 'The server returned no data for this report.',
              type: 'warning',
            });
          }
        },
        onError: (err) => {
          toaster.create({
            title: 'Export failed',
            description: err instanceof Error ? err.message : undefined,
            type: 'error',
          });
        },
      },
    );
  };

  if (reportQuery.isLoading) {
    return (
      <Center py={8}>
        <Spinner size="md" color="button.primary" />
      </Center>
    );
  }

  if (reportQuery.isError) {
    return (
      <Box py={4} data-e2e="event-report-error">
        <Text color="text.error" fontSize="sm">
          Failed to load report. Try reopening the drawer.
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" gap={4} data-e2e="event-report-panel">
      {/* Header: Export button + PII checkbox. Mirrors the top-right
          download trigger pattern in AlertHistoryEventsTab. */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Checkbox.Root
          checked={includePii}
          onCheckedChange={(details) =>
            handlePiiToggle(Boolean(details.checked))
          }
          size="sm"
          data-e2e="event-report-pii-checkbox"
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>
            <Text fontSize="sm">Include student PII (FERPA logged)</Text>
          </Checkbox.Label>
        </Checkbox.Root>

        <ButtonComponent
          visual="primary"
          onClick={handleExport}
          disabled={downloadMutation.isPending}
          aria-label="Export event report"
          data-e2e="event-report-export-button"
        >
          <HStack gap={2}>
            <MdDownload />
            <Text>
              {downloadMutation.isPending ? 'Exporting…' : 'Export'}
            </Text>
          </HStack>
        </ButtonComponent>
      </Flex>

      <SectionCard title="Event Summary">
        <VStack align="stretch" gap={0}>
          {summaryRows
            ? summaryRows.map((r) => (
                <DetailRow
                  key={r.label}
                  label={r.label}
                  value={r.value}
                  dataE2e={r.dataE2e}
                />
              ))
            : (
              <Text fontSize="sm" color="text.secondary" py={2}>
                Summary data unavailable for this event.
              </Text>
            )}
        </VStack>
      </SectionCard>

      <SectionCard title="Performance Metrics">
        <VStack align="stretch" gap={0}>
          {metricsRows
            ? metricsRows.map((r) => (
                <DetailRow
                  key={r.label}
                  label={r.label}
                  value={r.value}
                  dataE2e={r.dataE2e}
                />
              ))
            : (
              <Text fontSize="sm" color="text.secondary" py={2}>
                Metrics unavailable for this event.
              </Text>
            )}
        </VStack>
      </SectionCard>

      {/* FERPA confirmation dialog — opens when the operator flips the
          PII checkbox on, before we actually accept the new state. */}
      <DialogComponent
        isOpen={pendingPiiConfirm}
        onClose={handlePiiCancel}
        e2eTitle="event-report-pii-confirm"
        headingText="Include student PII in export?"
        size="md"
      >
        <Box p={4}>
          <VStack align="stretch" gap={3}>
            <Text fontSize="sm">
              The exported report will include student and guardian PII
              (names, grades). A FERPA audit-log entry will be written
              recording you as the exporter and the sections included.
            </Text>
            <Text fontSize="sm" color="text.secondary">
              Per ADR-099 §Layer 5, PII exports are tracked for compliance
              audits. You can still download without PII by leaving this
              option unchecked.
            </Text>
            <HStack justify="flex-end" gap={2} pt={2}>
              <ButtonComponent
                visual="secondary"
                onClick={handlePiiCancel}
                data-e2e="event-report-pii-confirm-cancel"
              >
                Cancel
              </ButtonComponent>
              <ButtonComponent
                visual="primary"
                onClick={handlePiiConfirm}
                data-e2e="event-report-pii-confirm-accept"
              >
                Include PII
              </ButtonComponent>
            </HStack>
          </VStack>
        </Box>
      </DialogComponent>
    </Flex>
  );
}
